/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  Output,
  OnInit,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { AbstractFormComponent, BuilderNavigationService } from '@gh/builder-shared';
import { SalesFolderStatus } from '@gh/builder-shared-data';
import {
  FormStateSnapshot,
  getFormStateSnapshotValue,
  getRouteParentTitle,
  ImmutablePageState,
  PAGE_STATE,
  queryDirty,
  queryFormMode,
  queryRecalculationNeeded,
  State,
} from '@gh/common-shared';
import { TranslationTupleOrId } from '@gh/core-mls';
import { FormMode } from '@gh/core-ui';
import { distinctUntilChanged$, filter$, map$, takeUntil$ } from '@gh/rx/operators';
import { Observable, Subject } from 'rxjs';
import { quoteDetailsReducer } from './hlp-quote-details.reducers';
import {
  queryQuoteDerivedInfo,
  queryQuoteMasterInfo,
  queryQuoteMasterInfoShadow,
  queryQuoteSettings,
  QUOTE_DETAILS_INIT_STATE,
  QuoteDetailsSettings,
  HlpQuoteDetailsState, queryQuoteToHlpModel,
} from './hlp-quote-details.state';
import {
  QuoteDetailsDerivedInfo,
  HlpQuoteDetailsMasterInfo,
  hlpQuoteDetailsInitFromDto,
} from '../../../resources/hlp-quote.model';
import { HlpQuoteDetailsCommandService } from './hlp-quote-details-command.service';
import { HlpDetailsBusinessService } from '../../../resources/hlp-details-business.service';

export function quoteDetailsStateFactory(): State<HlpQuoteDetailsState> {
  return new State(QUOTE_DETAILS_INIT_STATE, quoteDetailsReducer);
}

export function quoteDetailsFormFactory(state: State<HlpQuoteDetailsState>): ImmutablePageState<HlpQuoteDetailsState> {
  return new ImmutablePageState(state, {
    formMode: queryFormMode,
    dirty:    queryDirty,
  });
}

function getQuoteTitle(mode: FormMode): string {
  return {
    [FormMode.CreateMode]: 'lbl_quote_create',
    [FormMode.EditMode]:   'lbl_quote_edit',
    [FormMode.ViewMode]:   'lbl_quote_view',
  }[mode];
}

@Component({
  standalone: false,
  selector:        'gh-hlp-details-quote-tab',
  templateUrl:     'hlp-details-quote-tab.component.html',
  styleUrls:       ['hlp-details-quote-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers:       [
    { provide: State, useFactory: quoteDetailsStateFactory },
    { provide: PAGE_STATE, useFactory: quoteDetailsFormFactory, deps: [State] },
    HlpQuoteDetailsCommandService,
  ],
})
export class HlpDetailsQuoteTabComponent extends AbstractFormComponent<HlpQuoteDetailsState> implements OnInit, OnDestroy {
  @Input() formMode: number;

  @Output() onChange = new EventEmitter();
  @Output() onTabLeave = new EventEmitter();

  recalculationNeeded$: Observable<boolean>;
  masterInfo$: Observable<HlpQuoteDetailsMasterInfo>;
  derivedInfo$: Observable<QuoteDetailsDerivedInfo>;
  masterInfoShadowSnapshot$: Observable<FormStateSnapshot<HlpQuoteDetailsMasterInfo>>;
  description$: Observable<string>;
  formModeTitle$ = this.form.mode$.pipe(map$(getQuoteTitle));

  private destroy$ = new Subject<void>();

  constructor(private route: ActivatedRoute,
    @Inject(PAGE_STATE) public form: ImmutablePageState<HlpQuoteDetailsState>,
    private state: State<HlpQuoteDetailsState>,
    private navigationService: BuilderNavigationService,
    private commands: HlpQuoteDetailsCommandService,
    public businessService: HlpDetailsBusinessService,
  ) {
    super();
  }

  get customerName(): string {
    return this.state.project(queryQuoteDerivedInfo).customerName;
  }

  get salesFolderName(): string {
    return this.state.project(queryQuoteDerivedInfo).salesFolderName;
  }

  get code(): number {
    return this.state.project(queryQuoteDerivedInfo).code;
  }

  get createdDate(): Date {
    return this.state.project(queryQuoteDerivedInfo).created;
  }

  get settings(): QuoteDetailsSettings {
    return this.state.project(queryQuoteSettings);
  }

  get isCompleted(): boolean {
    const { salesFolderStatus, isCompletionQuote } = this.state.project(queryQuoteDerivedInfo);
    return salesFolderStatus === SalesFolderStatus.Completed && isCompletionQuote;
  }

  get parentTitle(): TranslationTupleOrId {
    return getRouteParentTitle(this.route) || 'lbl_quotations_list';
  }

  ngOnInit(): void {
    const init = hlpQuoteDetailsInitFromDto(this.businessService.hlpQuoteDetailsInitFromHlpDetailsDto(this.businessService.hlpDetails));
    const mode = this.formMode;

    this.recalculationNeeded$ = this.state.select(queryRecalculationNeeded);
    this.masterInfo$ = this.state.select(queryQuoteMasterInfo).pipe(map$(getFormStateSnapshotValue));
    this.masterInfoShadowSnapshot$ = this.state.select(queryQuoteMasterInfoShadow);
    this.description$ = this.masterInfo$.pipe(map$((masterInfo) => masterInfo.description));
    this.derivedInfo$ = this.state.select(queryQuoteDerivedInfo);
    this.commands.load(init, mode);
    this.businessService.quoteForm = this.form;

    this.state.select(queryDirty).pipe(
      distinctUntilChanged$(),
      filter$((dirty) => dirty === true),
      takeUntil$(this.destroy$),
    ).subscribe(() => this.onChange.emit());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.onTabLeave.emit(this.state.project(queryQuoteToHlpModel));
  }

  onSearchHousePlan(): void {
    this.commands.searchHousePlan();
  }

  onOpenGto(value: any): void {
    this.commands.openGto(value.isDisabledGto);
  }

  onOpenHpe(): void {
    this.commands.openHpe();
  }

  onOpenStr(): void {
    this.commands.openStr();
  }

  onOpenTops(value: any): void {
    this.commands.openTops(value.formMode);
  }

  onMasterInfoChange(masterInfo: FormStateSnapshot<HlpQuoteDetailsMasterInfo>): void {
    this.commands.setMasterInfo(masterInfo);
    this.onChange.emit();
  }

  onBaseHousePlanChange(): void {
    this.onChange.emit();
  }

  onVariationsChange(): void {
    this.onChange.emit();
  }

}
